import React, { useEffect, useMemo, useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface NoteNodeProps extends NodeProps {
  nodeId?: string;
  readOnly?: boolean;
}

export function NoteNode({
  id,
  data,
  selected,
  nodeId,
  readOnly,
}: NoteNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const setSelectedNode = useWorkflowStore((state) => state.setSelectedNode);
  const selectedNodeId = useWorkflowStore((state) => state.selectedNode);

  const rawConfig =
    typeof (data as { config?: unknown })?.config === 'object' &&
    (data as { config?: unknown }).config !== null
      ? ((data as { config?: Record<string, unknown> }).config as Record<
          string,
          unknown
        >)
      : ({} as Record<string, unknown>);
  const noteValue =
    typeof rawConfig.note === 'string' ? (rawConfig.note as string) : '';
  const fontSize =
    typeof rawConfig.fontSize === 'number'
      ? (rawConfig.fontSize as number)
      : 14;
  const safeTitle =
    typeof (data as { label?: unknown })?.label === 'string'
      ? ((data as { label?: string }).label as string)
      : t('Note');
  const safeStatus = (() => {
    const v = (data as { status?: unknown })?.status;
    return typeof v === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(v)
      ? (v as 'idle' | 'running' | 'success' | 'error')
      : undefined;
  })();

  const initialHtml = useMemo(() => {
    if (!noteValue) return '';
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(noteValue);
    return looksLikeHtml
      ? noteValue
      : noteValue
          .split(/\r?\n/)
          .map((line) => `<p>${line || ''}</p>`)
          .join('');
  }, [noteValue]);

  const [hasText, setHasText] = useState(
    Boolean(noteValue && noteValue.trim().length),
  );
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  const isStoreSelected = selectedNodeId === (nodeId || id);

  const changeFontSize = (delta: number) => {
    const newSize = Math.min(Math.max(fontSize + delta, 10), 32);
    if (newSize !== fontSize) {
      updateNodeData(id, {
        config: {
          ...rawConfig,
          fontSize: newSize,
        },
      });
    }
  };

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        horizontalRule: false,
        heading: {
          levels: [1, 2, 3],
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setHasText(editor.getText().trim().length > 0);
    },
    onFocus: () => {
      setIsEditorFocused(true);
      setSelectedNode(nodeId || id);
    },
    onBlur: ({ editor }) => {
      const html = editor.getHTML();
      updateNodeData(id, {
        config: {
          ...rawConfig,
          note: html,
        },
      });
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none text-sm dark:prose-invert px-3 py-2 min-h-[80px] prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
    editor.commands.setContent(initialHtml || '<p></p>');
    setHasText(Boolean(noteValue && noteValue.trim().length));
  }, [editor, initialHtml, noteValue]);

  useEffect(() => {
    if (editor && editor.view && editor.view.dom) {
      editor.view.dom.style.fontSize = `${fontSize}px`;
    }
  }, [editor, fontSize]);

  useEffect(() => {
    if (!isStoreSelected) {
      setIsEditorFocused(false);
    }
  }, [isStoreSelected]);

  return (
    <BaseNode
      title={safeTitle}
      icon={
        <svg
          className='w-4 h-4 text-gray-500'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={safeStatus}
      selected={isStoreSelected || isEditorFocused}
      nodeId={nodeId || id}
      readOnly={readOnly}
      minimal={true}
    >
      <div className='px-3 pt-2 pb-3 w-[400px]'>
        <div
          className={`w-full rounded-md overflow-hidden ${
            isStoreSelected || isEditorFocused
              ? 'bg-white dark:bg-slate-700/50 border border-gray-300 dark:border-white/5'
              : 'bg-transparent border-transparent'
          }`}
        >
          <div
            className='relative nodrag nopan'
            onMouseDownCapture={(e) => e.stopPropagation()}
            onPointerDownCapture={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {editor && <EditorContent editor={editor} />}
            {!hasText && (
              <div className='pointer-events-none absolute top-2 left-3 text-gray-400 dark:text-gray-500 text-sm'>
                {t('Add notes or documentation...')}
              </div>
            )}
            {editor && (isStoreSelected || isEditorFocused) && (
              <div className='border-t border-gray-200 dark:border-gray-600 flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-slate-800/60 text-xs'>
                <button
                  type='button'
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  }
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-bold ${
                    editor.isActive('heading', { level: 1 })
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  H1
                </button>
                <button
                  type='button'
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  }
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-bold ${
                    editor.isActive('heading', { level: 2 })
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  H2
                </button>
                <button
                  type='button'
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  }
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-bold ${
                    editor.isActive('heading', { level: 3 })
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  H3
                </button>
                <button
                  type='button'
                  onClick={() => editor.chain().focus().setParagraph().run()}
                  className={`px-1.5 py-0.5 rounded border text-[11px] ${
                    editor.isActive('paragraph')
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  P
                </button>
                <div className='w-px h-3 bg-gray-300 dark:bg-gray-600 mx-1' />
                <button
                  type='button'
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${
                    editor.isActive('bold')
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  B
                </button>
                <button
                  type='button'
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`px-1.5 py-0.5 rounded border text-[11px] italic ${
                    editor.isActive('italic')
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  I
                </button>
                <button
                  type='button'
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={`px-1.5 py-0.5 rounded border text-[11px] ${
                    editor.isActive('bulletList')
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  • List
                </button>
                <button
                  type='button'
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={`px-1.5 py-0.5 rounded border text-[11px] ${
                    editor.isActive('orderedList')
                      ? 'bg-gray-200 dark:bg-slate-700 border-gray-400 dark:border-gray-500'
                      : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  1. List
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseNode>
  );
}
